import { z } from 'zod';
import { UserSummary } from './auth';

// roles hold permissions; admins hold roles; permissions decide what the server allows
export const Permission = z.string().regex(/^[a-z]+(\.[a-z_]+)+$/, 'permission names look like content.places.write');
export type Permission = z.infer<typeof Permission>;

export const Role = z.object({
  id: z.string().uuid(),
  key: z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/),
  name: z.string().min(2).max(80),
  description: z.string().max(400).default(''),
  permissions: z.array(Permission),
  system: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Role = z.infer<typeof Role>;
export const RoleCreate = Role.pick({ key: true, name: true, description: true, permissions: true }).partial({
  description: true,
});
export type RoleCreate = z.infer<typeof RoleCreate>;
export const RoleUpdate = Role.pick({ name: true, description: true, permissions: true }).partial();
export type RoleUpdate = z.infer<typeof RoleUpdate>;

export const Admin = z.object({
  id: z.string().uuid(),
  user: UserSummary,
  roles: z.array(Role.pick({ id: true, key: true, name: true })),
  permissions: z.array(Permission),
  superAdmin: z.boolean(),
  disabledAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Admin = z.infer<typeof Admin>;
export const AdminCreate = z.object({ email: z.string().email(), roleIds: z.array(z.string().uuid()).min(1) });
export type AdminCreate = z.infer<typeof AdminCreate>;
export const AdminRolesUpdate = z.object({ roleIds: z.array(z.string().uuid()).min(1) });
export type AdminRolesUpdate = z.infer<typeof AdminRolesUpdate>;

export const AdminMeReply = z.object({ admin: Admin });
export type AdminMeReply = z.infer<typeof AdminMeReply>;

// two-step login (authenticator app codes), required for every admin
export const TwoStepStatus = z.enum(['setup_required', 'verify_required', 'verified']);
export type TwoStepStatus = z.infer<typeof TwoStepStatus>;
export const AdminSessionReply = z.object({
  admin: Admin,
  twoStep: z.object({ status: TwoStepStatus, recoveryCodesLeft: z.number().int().min(0) }),
});
export type AdminSessionReply = z.infer<typeof AdminSessionReply>;
export const TwoStepSetupReply = z.object({ secret: z.string(), otpauthUri: z.string() });
export type TwoStepSetupReply = z.infer<typeof TwoStepSetupReply>;
// a six-digit code from the app, or one recovery code (letters and digits, dashes ignored)
export const TwoStepCode = z.union([
  z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your app') }).strict(),
  z.object({ recoveryCode: z.string().min(8).max(20) }).strict(),
]);
export type TwoStepCode = z.infer<typeof TwoStepCode>;
export const RecoveryCodesReply = z.object({ recoveryCodes: z.array(z.string()) });
export type RecoveryCodesReply = z.infer<typeof RecoveryCodesReply>;
export const AdminListReply = z.object({ admins: z.array(Admin) });
export const RoleListReply = z.object({ roles: z.array(Role) });

// the append-only record of every admin action
export const AuditEntry = z.object({
  id: z.string().uuid(),
  actorUserId: z.string().uuid().nullable(),
  action: z.string(),
  subjectType: z.string(),
  subjectId: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  ip: z.string().nullable(),
  requestId: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;
export const AuditQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: z.string().datetime().optional(),
  action: z.string().optional(),
});
export const AuditListReply = z.object({ entries: z.array(AuditEntry) });
